import React, { useEffect, useState } from "react";
import { useMsal } from '@azure/msal-react';
import { createStatisticsService } from "../../utils/statistics.service";
import { UsersIcon, NewUsersIcon, TotalQueriesIcon } from "./StatisticsIcons";
import HorizontalBarChart from "./HorizontalBarChart";

/**
 * StatisticsPanel
 * Fetches and displays statistics data. Shows a spinner while loading.
 */
const StatisticsPanel: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const THREE_HOURS_MS = 3 * 60 * 60 * 1000;

  const { instance: msalInstance } = useMsal();

  const fetchData = React.useCallback((showSpinner: boolean) => {
    let mounted = true;
    if (showSpinner) setLoading(true);
    setError(null);
    const statisticsService = createStatisticsService(msalInstance);
    statisticsService.getStatistics()
      .then((result) => {
        if (mounted) {
          if (result?.horizontalBars?.labels && result?.horizontalBars?.values) {
            const paired = (result.horizontalBars.labels as string[]).map((label: string, i: number) => ({
              label,
              value: result.horizontalBars.values[i] as number,
            }));
            paired.sort((a, b) => b.value - a.value);
            result.horizontalBars.labels = paired.map(p => p.label);
            result.horizontalBars.values = paired.map(p => p.value);
          }
          setData(result);
          setLastUpdated(new Date());
          if (showSpinner) setLoading(false);
        }
      })
      .catch((err) => {
        if (mounted) {
          setError(err?.message || "Failed to load statistics");
          if (showSpinner) setLoading(false);
        }
      });
    return () => { mounted = false; };
  }, [msalInstance]);

  useEffect(() => {
    const cleanup = fetchData(true);
    const interval = setInterval(() => fetchData(false), THREE_HOURS_MS);
    return () => {
      cleanup?.();
      clearInterval(interval);
    };
  }, [fetchData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Accepts a string in UTC (e.g., 2026-05-13T09:52:53.490000) or a Date object
  const formatLastUpdated = (input: string | Date) => {
    let date: Date;
    if (typeof input === 'string') {
      // Remove extra microseconds if present (e.g., .490000 → .490)
      const cleaned = input.replace(/\.(\d{3})\d+$/, '.$1');
      // Parse as UTC
      date = new Date(cleaned + 'Z');
    } else {
      date = input;
    }
    // Now is local time
    const now = new Date();
    const isToday = date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth() &&
      date.getDate() === now.getDate();
    if (isToday) {
      return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    }
    return date.toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  };

  // Layout: left 1/4 (KPI), right 3/4 (bar chart)
  if (loading) {
    return (
      <div className="flex mt-6 pb-6 h-full w-full items-center justify-center bg-transparent border-b" style={{ borderColor: 'var(--dark-color-border-color)' }}>
        <div className="flex flex-col items-center justify-center w-full">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-500 mb-4" />
          <div className="text-base text-center" style={{ color: 'var(--dark-color-text)' }}>Loading metrics…</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex mt-6 pb-6 h-full w-full items-center justify-center bg-transparent border-b" style={{ borderColor: 'var(--dark-color-border-color)' }}>
        <div className="text-xl font-semibold text-center w-full" style={{ color: 'var(--dark-color-text)' }}>
          Cannot fetch Navigator metrics. Please try again later.
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full bg-transparent border-b" style={{ borderColor: 'var(--dark-color-border-color)' }}>
      {/* Left: KPIs */}
      <div
        className="w-1/4 shrink-0 flex flex-col items-start justify-start pt-8 pr-6 pb-8 pl-8 border-r"
        style={{ background: 'var(--dark-main-background)', borderColor: 'var(--dark-color-border-color)'}}
      >
        <div
          className="text-sm mb-3 text-left w-full"
          style={{ color: 'var(--dark-color-text)' }}
        >
          Total usage for 2026
        </div>
        {/* KPI cards container — centered in remaining space */}
        <div className="flex-1 flex flex-col justify-between w-full py-2">
          {/* KPI 1 - Total Users */}
          <div className="flex items-center w-full px-6 py-6">
            <UsersIcon size={42} />
            <div className="ml-4">
              <div className="text-xs font-medium" style={{ color: 'var(--dark-color-text)' }}>Total Users</div>
              <div className="text-lg font-bold" style={{ color: 'var(--dark-color-text)' }}>{
                typeof data?.cards?.[0]?.value === 'number' ? data.cards[0].value.toLocaleString() : '-'
              }</div>
            </div>
          </div>
          <div className="w-full h-px" style={{ background: 'var(--dark-color-border-color)' }} />
          {/* KPI 2 - New Users */}
          <div className="flex items-center w-full px-6 py-6">
            <NewUsersIcon size={35} />
            <div className="ml-4">
              <div className="text-xs font-medium" style={{ color: 'var(--dark-color-text)' }}>New Users</div>
              <div className="text-lg font-bold" style={{ color: 'var(--dark-color-text)' }}>{
                typeof data?.cards?.[2]?.value === 'number' ? data.cards[2].value.toLocaleString() : '-'
              }</div>
            </div>
          </div>
          <div className="w-full h-px" style={{ background: 'var(--dark-color-border-color)' }} />
          {/* KPI 3 - Total Queries */}
          <div className="flex items-center w-full px-6 py-6">
            <TotalQueriesIcon size={32} />
            <div className="ml-4">
              <div className="text-xs font-medium" style={{ color: 'var(--dark-color-text)' }}>Total Queries</div>
              <div className="text-lg font-bold" style={{ color: 'var(--dark-color-text)' }}>{
                typeof data?.cards?.[1]?.value === 'number' ? data.cards[1].value.toLocaleString() : '-'
              }</div>
            </div>
          </div>
        </div>
      </div>
      {/* Right: Bar Chart */}
      <div
        className="flex-1 flex flex-col items-center justify-center px-12 py-8 relative"
        style={{ background: 'var(--dark-color-accent)' }}
      >
        {/* Last updated timestamp — top-right corner, vertically centered with the X button */}
        {lastUpdated && (
          <div
            className="absolute top-4 right-12 text-sm opacity-60 flex items-center"
            style={{ color: 'var(--dark-color-text)', height: '1.75rem' }}
          >
            Last Updated: {formatLastUpdated(data?.cards?.[3]?.value )}
          </div>
        )}
        {data?.horizontalBars ? (
          <HorizontalBarChart
            labels={data.horizontalBars.labels}
            values={data.horizontalBars.values}
            title={data.horizontalBars.title || "Top Capabilities"}
          />
        ) : null}
      </div>
    </div>
  );
};

export default StatisticsPanel;